import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import clsx from 'clsx'
import ArrowRight from 'components/Icons/ArrowRight'
import Checkmark from 'components/Icons/Checkmark'
import CrossCircleFilled from 'components/Icons/CrossCircleFilled'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import AppIcon from '../preview/AppIcon'
import BigLeaguesPhone from './BigLeaguesPhone'
import './whyItMatters.scss'

const t = buildTranslate('live_updates.overview.why')

const CARD_GAP = '14px'
// The prototype's one-card mock fills 40% of its progress bar.
const ONE_CARD_PROGRESS = '40%'

type NotificationChip = {
  initials: string
  name: string
  time: string
  sub: string
}

// Built inside the component (not at module scope) so it reads translations
// only after locale data is loaded, not at import time. `initials` match the
// Sketch prototype's literal avatar text ("CA", "··", "DP") — plain text
// rather than an icon, since a sender avatar in a real notification is text,
// not an icon-library glyph.
const notificationChips = (): NotificationChip[] => [
  {
    initials: 'CA',
    name: t('without_notifications.courier_name'),
    time: t('without_notifications.courier_time'),
    sub: t('without_notifications.courier_sub'),
  },
  {
    initials: '··',
    name: t('without_notifications.sms_name'),
    time: t('without_notifications.sms_time'),
    sub: t('without_notifications.sms_sub'),
  },
  {
    initials: 'DP',
    name: t('without_notifications.delivery_name'),
    time: t('without_notifications.delivery_time'),
    sub: t('without_notifications.delivery_sub'),
  },
]

// A lock screen cropped by the panel it sits in — it runs off the bottom edge
// rather than being drawn as a complete phone (Sketch prototype `.miniph`).
const CroppedPhone = ({
  children,
  badge,
}: {
  children: React.ReactNode
  /** Carries its own filled circle — the badge frame only rings it in white. */
  badge: React.ReactNode
}) => (
  <div className="lu-why-phone">
    <span className="lu-why-badge">{badge}</span>
    <span className="lu-why-notch" />
    <div className="lu-why-time">{t('clock')}</div>
    <div className="lu-why-stack">{children}</div>
  </div>
)

// The card footer both cards share: a fixed-height block so the two cards'
// illustrations line up however their captions wrap (Sketch prototype `.wtext`).
const CardCaption = ({ title, body }: { title: string; body: string }) => {
  const [css] = useStyletron()
  return (
    <div
      className={css({
        flex: 'none',
        height: '84px',
        boxSizing: 'border-box',
        padding: '16px 20px 20px',
      })}
    >
      <Text.BodyText4 bold color="grey90" className={css({ display: 'block' })}>
        {title}
      </Text.BodyText4>
      <Text.BodyText5
        color="grey70"
        className={css({ display: 'block', marginTop: '5px' })}
      >
        {body}
      </Text.BodyText5>
    </div>
  )
}

export interface WhyItMattersSectionProps {
  /** The merchant's uploaded logo — the "with Live Updates" mock uses this
   * (and merchantName) instead of a generic placeholder, same as the hero. */
  appIconUrl: string | undefined
  merchantName: string | undefined
}

// Static education (Sketch prototype live-updates-onboarding v31): the live
// activities guests already get from big consumer brands, and a before/after
// pair — a pile of notification chips from senders a guest doesn't recognize,
// versus one clean, on-brand live-activity card.
const WhyItMattersSection = ({
  appIconUrl,
  merchantName,
}: WhyItMattersSectionProps) => {
  const [css] = useStyletron()
  const chips = notificationChips()

  const cardClass = css({
    flex: '1 1 320px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    backgroundColor: palette.white,
    border: `1px solid ${palette.grey30}`,
    overflow: 'hidden',
  })

  return (
    <div>
      <Text.Header3
        tag="h2"
        className={css({ margin: '0 0 16px', paddingTop: '8px' })}
      >
        {t('title')}
      </Text.Header3>
      <div
        className={css({
          display: 'flex',
          gap: CARD_GAP,
          flexWrap: 'wrap',
          alignItems: 'stretch',
        })}
      >
        <div data-testid="lu-overview-reasons" className={cardClass}>
          <div
            className={clsx(
              'lu-why-img',
              css({
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                padding: '24px 20px 0',
              })
            )}
          >
            <BigLeaguesPhone />
          </div>
          <CardCaption
            title={t('big_leagues.heading')}
            body={t('big_leagues.body')}
          />
        </div>

        <div data-testid="lu-overview-comparison" className={cardClass}>
          <div
            aria-hidden="true"
            className={clsx('lu-why-img', 'lu-why-compare', css({ flex: 1 }))}
          >
            <div className="lu-why-side">
              <span
                className={`lu-why-lab ${css({
                  color: palette.cayenne,
                  backgroundColor: palette.cayenne05,
                })}`}
              >
                {t('without_title')}
              </span>
              <CroppedPhone badge={<CrossCircleFilled />}>
                {chips.map(chip => (
                  <div key={chip.name} className="lu-why-chip">
                    <span className="lu-why-chip-av">{chip.initials}</span>
                    <div className="lu-why-chip-txt">
                      <div className="lu-why-chip-row">
                        <b>{chip.name}</b>
                        <span>{chip.time}</span>
                      </div>
                      <div className="lu-why-chip-sub">{chip.sub}</div>
                    </div>
                  </div>
                ))}
              </CroppedPhone>
            </div>
            <span className="lu-why-arrow">
              <ArrowRight />
            </span>
            <div className="lu-why-side">
              <span
                className={`lu-why-lab ${css({
                  color: palette.spearmint60,
                  backgroundColor: palette.spearmint10,
                  // The prototype gives the positive side the louder label.
                  fontSize: '11px',
                })}`}
              >
                {t('with_title')}
              </span>
              <CroppedPhone badge={<Checkmark />}>
                <div className="lu-why-onecard">
                  <div className="lu-why-one-row">
                    <AppIcon appIconUrl={appIconUrl} size="18px" radius="5px" />
                    <span>{merchantName ?? t('with_card.brand')}</span>
                    <span>{t('with_card.eta')}</span>
                  </div>
                  <p className="lu-why-one-text">
                    <b>{t('with_card.status')}</b>
                    {t('with_card.sub')}
                  </p>
                  <div className="lu-why-one-bar">
                    <span style={{ width: ONE_CARD_PROGRESS }} />
                  </div>
                </div>
              </CroppedPhone>
            </div>
          </div>
          <CardCaption title={t('compare_title')} body={t('compare_body')} />
        </div>
      </div>
    </div>
  )
}

export default WhyItMattersSection
